import React, { useEffect, useState } from 'react';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Grid from '@material-ui/core/Grid';
import { DataGrid } from '@afosto/app-components-react/DataGrid';
import DataGridPreview from '@afosto/app-components-react/DataGrid/DataGridPreview';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import Page from '@afosto/app-components-react/Page';
import OauthClientForm from '../../components/OauthClientForm';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import { clientNameFormatter } from './formatters';
import { oauth as oauthClientsApi } from '@afosto/iam/api/Oauth';
import translations from './translations';

const fetchClients = async dataSource =>
  dataSourceRequest(
    dataSource,
    async (data, requestOptions) => oauthClientsApi.listClients({ isDeleted: 0 }, requestOptions),
    {
      transformData: data =>
        data.reduce(
          (acc, oauthClientData) => [
            ...acc,
            {
              id: oauthClientData.client_id,
              clientId: oauthClientData.client_id,
              name: oauthClientData.name,
              grantTypes: oauthClientData.grant_types,
              redirectUri: oauthClientData.redirect_uri,
              createdAt: oauthClientData.created_at,
            },
          ],
          [],
        ),
    },
  );

const OauthClients = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['iam:oauth:read']);

  const history = useHistory();
  const intl = useIntl();
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();
  const [oauthClient, setOauthClient] = useState(null);
  const [isFetchingOauthClient, setIsFetchingOauthClient] = useState(false);
  const [showCreateOauthClient, setShowCreateOauthClient] = useState(false);
  const [showEditOauthClient, setShowEditOauthClient] = useState(false);

  const dataSource = useDataGrid(fetchClients, {
    modules: ['dataProperties', 'pagination', 'activeItem'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'name',
          name: intl.formatMessage({
            id: 'dataGrid.columns.oauthClients.name',
            defaultMessage: 'Name',
            description: 'The column name for column name',
          }),
          type: 'oauthClient',
        },
        {
          key: 'grantTypes',
          name: intl.formatMessage({
            id: 'dataGrid.columns.oauthClients.grantTypes',
            defaultMessage: 'Grant types',
            description: 'The column name for column grant types',
          }),
          type: 'chips',
        },
        {
          key: 'redirectUri',
          name: intl.formatMessage({
            id: 'dataGrid.columns.oauthClients.redirectUri',
            defaultMessage: 'Redirect uri',
            description: 'The column name for column redirect uri',
          }),
          type: 'string',
        },
        {
          key: 'createdAt',
          name: intl.formatMessage({
            id: 'dataGrid.columns.oauthClients.createdAt',
            defaultMessage: 'Created at',
            description: 'The column name for column created at',
          }),
          type: 'date',
        },
      ],
    },
  });
  const { activeItem: activeItemModule = {} } = dataSource;
  const { activeItem } = activeItemModule;

  /**
   * Fetch the oauth client data if a oauth client row is selected and show the data in the preview.
   */
  useEffect(() => {
    let isMounted = true;

    if (activeItem) {
      const fetchOauthClient = async id => {
        setIsFetchingOauthClient(true);

        try {
          const response = await oauthClientsApi.getClient(id);
          const { data = {} } = response;

          if (isMounted) {
            setOauthClient(data);
          }
        } catch (err) {
          if (isMounted) {
            setOauthClient(null);
          }
        } finally {
          if (isMounted) {
            setIsFetchingOauthClient(false);
          }
        }
      };

      fetchOauthClient(activeItem);
    } else if (isMounted) {
      setOauthClient(null);
    }

    return () => {
      isMounted = false;
    };
  }, [activeItem]);

  const handleSaveOauthClient = async ({ id, name, scopes }) => {
    try {
      const response = await oauthClientsApi.updateClient(id, {
        name,
        scope: scopes.map(option => option?.label ?? option),
      });
      setOauthClient(response?.data ?? null);
      dataSource.reloadDataSource();
      setShowEditOauthClient(false);

      enqueueSnackbar(intl.formatMessage(translations.feedback.oauthClientUpdated), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.oauthClientNotUpdated), {
        variant: 'error',
      });
    }
  };
  const handleDeleteOauthClient = async id => {
    try {
      const confirmResponse = await confirm({
        title: intl.formatMessage(translations.oauthClient.confirmDeleteClient.title),
        description: intl.formatMessage(translations.oauthClient.confirmDeleteClient.description),
        catchOnCancel: false,
        buttons: [
          {
            label: intl.formatMessage(translations.oauthClient.confirmDeleteClient.buttons.cancel),
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage(translations.oauthClient.confirmDeleteClient.buttons.delete),
            response: 'delete',
            type: 'submit',
            variant: 'contained',
            color: 'primary',
          },
        ],
      });

      if (confirmResponse === 'delete') {
        const response = await oauthClientsApi.deleteClient(id);
        dataSource.activeItem.setActiveItem(null);
        dataSource.reloadDataSource();
        return response.data;
      }

      return null;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  /**
   * Handle the onSubmit event of the oauth client form in the create new oauth client drawer.
   * @param {object} oauthClientData
   * @returns {Promise}
   */
  const handleCreateOauthClient = async oauthClientData => {
    try {
      const response = await oauthClientsApi.createClient({
        name: oauthClientData.name,
        redirect_uri: oauthClientData.redirectUri,
        grant_types: oauthClientData.grantTypes.map(option => option.value),
        scope: oauthClientData.scopes.map(option => option.label),
      });
      setShowCreateOauthClient(false);
      dataSource.reloadDataSource();
      return response.data;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  const previewMenu = [
    ...(oauthClient
      ? [
          {
            key: 'client_view',
            translationKey: 'actions.view',
            label: 'View',
            icon: 'eye',
            shortcut: true,
            onClick: () => history.push(`/oauth-clients/${oauthClient.client_id}`),
          },
          ...(hasPermissions(['iam:oauth:write'])
            ? [
                {
                  key: 'client_edit',
                  translationKey: 'actions.edit',
                  label: 'Edit',
                  icon: 'pencil',
                  shortcut: true,
                  onClick: () => setShowEditOauthClient(true),
                },
                {
                  key: 'client_delete',
                  translationKey: 'app.users.preview.delete',
                  label: 'Delete',
                  icon: 'trash-alt',
                  color: 'secondary',
                  onClick: () => handleDeleteOauthClient(oauthClient.id),
                },
              ]
            : []),
        ]
      : []),
  ];

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  return (
    <Grid container wrap="nowrap">
      <Page
        title={intl.formatMessage(translations.title)}
        fullWidth
        style={{ flexGrow: 1 }}
        primaryAction={
          hasPermissions(['iam:oauth:write'])
            ? {
                text: intl.formatMessage(translations.oauthClient.create.button),
                action: () => setShowCreateOauthClient(true),
              }
            : null
        }
      >
        <DataGrid dataSource={dataSource} formatters={{ oauthClient: clientNameFormatter }} />
      </Page>
      <DataGridPreview
        title={oauthClient ? oauthClient.name : null}
        menu={previewMenu}
        open={dataSource.activeItem.activeItem !== null}
        onClose={() => dataSource.activeItem.setActiveItem(null)}
      >
        <LoadingWrapper isLoading={isFetchingOauthClient}>
          {oauthClient && (
            <OauthClientForm inline oauthClient={oauthClient} onSubmit={handleSaveOauthClient} />
          )}
        </LoadingWrapper>
      </DataGridPreview>
      <FormDrawer
        title={intl.formatMessage(translations.oauthClient.create.title)}
        isOpen={showCreateOauthClient}
        onClose={() => setShowCreateOauthClient(false)}
        drawerWidth="400px"
      >
        <OauthClientForm
          onSubmit={handleCreateOauthClient}
          onCancel={() => setShowCreateOauthClient(false)}
        />
      </FormDrawer>
      <FormDrawer
        title={intl.formatMessage(translations.oauthClient.edit.title)}
        isOpen={showEditOauthClient}
        onClose={() => setShowEditOauthClient(false)}
        drawerWidth="400px"
      >
        <OauthClientForm
          oauthClient={oauthClient}
          onCancel={() => setShowEditOauthClient(false)}
          onSubmit={handleSaveOauthClient}
        />
      </FormDrawer>
    </Grid>
  );
};

export default OauthClients;
